import React, { useState, useRef, useEffect } from 'react';
import { DragDropContext, Droppable, Draggable, DropResult } from '@hello-pangea/dnd';
import { toast } from 'sonner';
import { Plus, MoreVertical, Pencil, Trash2, Palette, Lock, Minimize2, Maximize2, Check, Pipette } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubTrigger,
  DropdownMenuSubContent,
} from '@/components/ui/dropdown-menu';
import type { PipelineState, Stage, Deal } from './types';
import { DealCard } from './DealCard';
import { NewDealDialog } from './NewDealDialog';
import { DealDetailDialog } from './DealDetailDialog';

interface PipelineBoardProps {
  state: PipelineState;
  onMoveDeal: (DealId: string, toStageId: string, position: number) => Promise<void>;
  onAddDeal: (data: any) => Promise<void>;
  onUpdateDeal: (DealId: string, updates: any) => Promise<void>;
  onMarkWon: (DealId: string, reason?: string) => Promise<void>;
  onMarkLost: (DealId: string, reason: string) => Promise<void>;
  onReopenDeal: (DealId: string, stageId: string) => Promise<void>;
  onDeleteDeal: (DealId: string) => Promise<void>;
  onUpdateStageTitle: (stageId: string, title: string) => Promise<void>;
  onUpdateStageBgColor: (stageId: string, bgColor: string) => Promise<void>;
  onDeleteStage: (stageId: string) => Promise<void>;
  onReorderStages: (newOrder: string[]) => Promise<void>;
}

export const colorPresets = [
  { name: "Default", value: "", bgClass: "bg-muted/70", hex: "#f1f5f9", borderClass: "border-border", stripHex: undefined },
  { name: "Blue", value: "bg-board-column-blue", bgClass: "bg-blue-100 dark:bg-blue-950/70", hex: "#dbeafe", borderClass: "border-blue-300 dark:border-blue-800", stripHex: "#3b82f6" },
  { name: "Green", value: "bg-board-column-green", bgClass: "bg-emerald-100 dark:bg-emerald-950/70", hex: "#dcfce7", borderClass: "border-emerald-300 dark:border-emerald-800", stripHex: "#10b981" },
  { name: "Red", value: "bg-board-column-red", bgClass: "bg-rose-100 dark:bg-rose-950/70", hex: "#fee2e2", borderClass: "border-rose-300 dark:border-rose-800", stripHex: "#ef4444" },
  { name: "Accent", value: "bg-board-column-primary", bgClass: "bg-purple-100 dark:bg-purple-950/70", hex: "#f3e8ff", borderClass: "border-purple-300 dark:border-purple-800", stripHex: "var(--workspace-accent)" },
  { name: "Yellow", value: "bg-board-column-yellow", bgClass: "bg-amber-100 dark:bg-amber-950/70", hex: "#fef9c3", borderClass: "border-amber-300 dark:border-amber-800", stripHex: "#f59e0b" },
  { name: "Orange", value: "bg-board-column-orange", bgClass: "bg-orange-100 dark:bg-orange-950/70", hex: "#ffedd5", borderClass: "border-orange-300 dark:border-orange-800", stripHex: "#f97316" },
];

// ─── Stage Column ────────────────────────────────────────────────
interface StageColumnProps {
  stage: Stage;
  Deals: Deal[];
  index: number;
  onAddDeal: (stageId: string) => void;
  onEditDeal: (Deal: Deal) => void;
  onUpdateStageTitle: (stageId: string, title: string) => Promise<void>;
  onUpdateStageBgColor: (stageId: string, bgColor: string) => Promise<void>;
  onDeleteStage: (stageId: string) => Promise<void>;
}

const StageColumn: React.FC<StageColumnProps> = ({
  stage, Deals, index, onAddDeal, onEditDeal,
  onUpdateStageTitle, onUpdateStageBgColor, onDeleteStage,
}) => {
  const [editingTitle, setEditingTitle] = useState(false);
  const [titleDraft, setTitleDraft] = useState(stage.title);
  const titleRef = useRef<HTMLInputElement>(null);
  const [isCollapsed, setIsCollapsed] = useState(false);
  const isProtected = stage.isWon || stage.isLost;

  useEffect(() => { if (editingTitle) titleRef.current?.focus(); }, [editingTitle]);

  const totalValue = Deals.reduce((sum, d) => sum + (d.value || 0), 0);

  const handleTitleSave = async () => {
    if (titleDraft.trim() && titleDraft !== stage.title) {
      await onUpdateStageTitle(stage.id, titleDraft.trim());
    }
    setEditingTitle(false);
  };

  const currentBgColor = stage.bgColor || "";
  const isCustomColor = Boolean(
    currentBgColor && (currentBgColor.startsWith('#') || currentBgColor.startsWith('rgb') || currentBgColor.startsWith('hsl'))
  );
  const preset = colorPresets.find(p => p.value === currentBgColor || (p.name === "Default" && (currentBgColor === "bg-board-column" || !currentBgColor)));

  const headerColor = isCustomColor 
    ? currentBgColor 
    : (preset?.stripHex || (stage.isWon ? '#10b981' : stage.isLost ? '#ef4444' : undefined));

  const columnBgStyle = isCustomColor 
    ? { backgroundColor: currentBgColor } 
    : undefined;

  const columnClass = isCustomColor 
    ? "" 
    : (currentBgColor || 'bg-board-column');

  const isCustomColorActive = Boolean(
    currentBgColor && !colorPresets.some((p) => p.value === currentBgColor || (p.name === "Default" && currentBgColor === "bg-board-column"))
  );

  return (
    <Draggable draggableId={`stage-${stage.id}`} index={index} isDragDisabled={isProtected}>
      {(provided, snapshot) => (
        <div
          ref={provided.innerRef}
          {...provided.draggableProps}
          className={`flex flex-col rounded-2xl ${isCollapsed ? 'w-14 cursor-pointer hover:bg-accent/30' : 'min-w-[280px] w-[280px]'} border border-border shadow-sm ${columnClass}
            ${snapshot.isDragging ? 'shadow-2xl ring-2 ring-workspace-accent/50' : ''}
          `}
          style={{
            ...provided.draggableProps.style,
            ...columnBgStyle,
          }}
          onClick={isCollapsed ? () => setIsCollapsed(false) : undefined}
        >
          <div
            {...provided.dragHandleProps}
            className={isCollapsed
              ? "flex flex-col items-center gap-2 p-2 py-4 h-fit min-h-[160px] cursor-grab active:cursor-grabbing hover:bg-muted/30 transition-colors rounded-2xl"
              : "px-3 pt-3 pb-2 flex items-center gap-2 select-none"
            }
          >
            {isCollapsed ? (
              <>
                <button
                  className="p-1 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted/50 transition-colors flex-shrink-0"
                  onClick={(e) => {
                    e.stopPropagation();
                    setIsCollapsed(false);
                  }}
                  title="Expand column"
                >
                  <Maximize2 className="h-4 w-4" />
                </button>
                <div className="flex-1" />
                <div className="writing-mode-vertical text-center flex flex-col-reverse items-center gap-2 mb-2">
                  <span className="text-sm font-semibold whitespace-nowrap" style={{ writingMode: 'vertical-rl', textOrientation: 'mixed', transform: 'rotate(180deg)' }}>
                    {stage.title}
                  </span>
                  <span className="text-xs font-bold text-muted-foreground bg-muted/50 rounded-full px-1.5 py-0.5 min-w-[20px] text-center" style={{ writingMode: 'vertical-rl', textOrientation: 'mixed', transform: 'rotate(180deg)' }}>
                    {Deals.length}
                  </span>
                </div>
              </>
            ) : (
              <>
                {/* Color strip */}
                {headerColor && (
                  <div className="w-1 h-5 rounded-full shrink-0" style={{ backgroundColor: headerColor }} />
                )}

            {editingTitle ? (
              <div className="flex-1 flex items-center gap-2 overflow-hidden">
                <input
                  ref={titleRef}
                  value={titleDraft}
                  onChange={e => setTitleDraft(e.target.value)}
                  onKeyDown={e => {
                    if (e.key === 'Enter') handleTitleSave();
                    if (e.key === 'Escape') { setTitleDraft(stage.title); setEditingTitle(false); }
                  }}
                  onBlur={handleTitleSave}
                  className="flex-1 text-sm font-semibold bg-transparent border-b border-workspace-accent outline-none text-foreground min-w-0"
                />
                <span className="text-xs font-bold text-muted-foreground bg-muted/50 rounded-full px-1.5 py-0.5 min-w-[20px] text-center shrink-0">
                  ${totalValue >= 1000 ? `${(totalValue / 1000).toFixed(0)}k` : totalValue.toFixed(0)}
                </span>
              </div>
            ) : (
              <div className="flex-1 flex items-center gap-2 overflow-hidden">
                <span className="text-sm font-semibold text-foreground truncate">
                  {stage.title}
                </span>
                <span className="text-xs font-bold text-muted-foreground bg-muted/50 rounded-full px-1.5 py-0.5 min-w-[20px] text-center shrink-0">
                  ${totalValue >= 1000 ? `${(totalValue / 1000).toFixed(0)}k` : totalValue.toFixed(0)}
                </span>
              </div>
            )}

            {/* Lock icon for protected */}
            {isProtected && <Lock className="w-3 h-3 text-muted-foreground/40 shrink-0" />}

            {/* Minimize */}
            <button
              onClick={() => setIsCollapsed(true)}
              className="p-1 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted/50 transition-colors"
              title="Collapse stage"
            >
              <Minimize2 className="w-3.5 h-3.5" />
            </button>

            {/* Menu */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  type="button"
                  className="p-1 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted/50 transition-colors"
                  aria-label="Stage options"
                >
                  <MoreVertical className="w-3.5 h-3.5" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-48 bg-popover border-border shadow-lg rounded-xl p-1 z-50">
                {!isProtected && (
                  <DropdownMenuItem
                    onClick={() => setEditingTitle(true)}
                    className="flex items-center gap-2 cursor-pointer"
                  >
                    <Pencil className="h-4 w-4" />
                    <span>Rename</span>
                  </DropdownMenuItem>
                )}

                {/* Color Submenu with circular single-row inline palette and custom color option */}
                <DropdownMenuSub>
                  <DropdownMenuSubTrigger className="flex items-center gap-2 cursor-pointer">
                    <Palette className="h-4 w-4" />
                    <span>Color</span>
                  </DropdownMenuSubTrigger>
                  <DropdownMenuSubContent className="p-2 bg-card border-border shadow-lg rounded-xl z-50">
                    <div className="space-y-1.5">
                      <div className="text-[10px] font-semibold text-primary uppercase tracking-wider px-0.5">
                        STAGE COLOR
                      </div>
                      <div className="flex items-center gap-1.5 py-0.5">
                        {colorPresets.map((p) => {
                          const isSelected =
                            (!currentBgColor && !p.value) ||
                            currentBgColor === p.value ||
                            (p.name === "Default" && (currentBgColor === "bg-board-column" || !currentBgColor));
                          return (
                            <button
                              key={p.name}
                              type="button"
                              onClick={() => onUpdateStageBgColor(stage.id, p.value)}
                              className={`relative w-5 h-5 rounded-full border transition-all hover:scale-115 flex items-center justify-center shrink-0 cursor-pointer shadow-xs ${p.bgClass} ${p.borderClass} ${
                                isSelected
                                  ? "ring-2 ring-primary ring-offset-1 ring-offset-background scale-105 border-primary"
                                  : "hover:ring-1 hover:ring-primary/40"
                              }`}
                              title={p.name}
                            >
                              {isSelected && (
                                <Check className="h-2.5 w-2.5 text-foreground drop-shadow-xs" strokeWidth={3} />
                              )}
                            </button>
                          );
                        })}

                        {/* Custom Color Option */}
                        <label
                          className={`relative w-5 h-5 rounded-full border transition-all hover:scale-115 flex items-center justify-center shrink-0 cursor-pointer shadow-xs ${
                            isCustomColorActive
                              ? "ring-2 ring-primary ring-offset-1 ring-offset-background scale-105 border-primary"
                              : "border-border/60 hover:ring-1 hover:ring-primary/40"
                          }`}
                          style={
                            isCustomColorActive
                              ? { backgroundColor: currentBgColor }
                              : {
                                  background:
                                    "conic-gradient(from 180deg at 50% 50%, #f43f5e 0deg, #fbbf24 60deg, #34d399 120deg, #38bdf8 180deg, #818cf8 240deg, #c084fc 300deg, #f43f5e 360deg)",
                                }
                          }
                          title="Custom color"
                        >
                          <input
                            type="color"
                            value={currentBgColor?.startsWith("#") ? currentBgColor : "#3b82f6"}
                            onChange={(e) => onUpdateStageBgColor(stage.id, e.target.value)}
                            className="sr-only"
                          />
                          {isCustomColorActive ? (
                            <Check className="h-2.5 w-2.5 text-white drop-shadow-md" strokeWidth={3} />
                          ) : (
                            <div className="w-3 h-3 rounded-full bg-background flex items-center justify-center shadow-xs">
                              <Pipette className="h-1.5 w-1.5 text-foreground" />
                            </div>
                          )}
                        </label>
                      </div>
                    </div>
                  </DropdownMenuSubContent>
                </DropdownMenuSub>

                {!isProtected && (
                  <>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem
                      onClick={() => onDeleteStage(stage.id)}
                      className="flex items-center gap-2 text-destructive focus:text-destructive focus:bg-destructive/10 cursor-pointer"
                    >
                      <Trash2 className="h-4 w-4" />
                      <span>Delete stage</span>
                    </DropdownMenuItem>
                  </>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
            </>
            )}
          </div>

          {/* Removed Deal count completely */}

          {/* Droppable Deal list */}
          <Droppable droppableId={stage.id} type="Deal">
            {(dropProvided, dropSnapshot) => (
              <div
                ref={dropProvided.innerRef}
                {...dropProvided.droppableProps}
                className={`flex-1 overflow-y-auto transition-colors rounded-xl ${
                  isCollapsed ? 'hidden' : 'px-3 pb-2 space-y-2 min-h-[60px]'
                } ${dropSnapshot.isDraggingOver ? 'bg-workspace-accent/5' : ''}`}
              >
                {!isCollapsed && Deals.map((Deal, DealIndex) => (
                  <Draggable key={Deal.id} draggableId={Deal.id} index={DealIndex}>
                    {(dragProvided, dragSnapshot) => (
                      <div
                        ref={dragProvided.innerRef}
                        {...dragProvided.draggableProps}
                        {...dragProvided.dragHandleProps}
                      >
                        <DealCard
                          deal={Deal}
                          onClick={() => onEditDeal(Deal)}
                          isDragging={dragSnapshot.isDragging}
                        />
                      </div>
                    )}
                  </Draggable>
                ))}
                {dropProvided.placeholder}
              </div>
            )}
          </Droppable>

          {/* Add Deal button */}
          <button
            onClick={(e) => { e.stopPropagation(); onAddDeal(stage.id); }}
            className={isCollapsed 
              ? "mx-auto mb-3 mt-auto flex items-center justify-center w-8 h-8 shrink-0 rounded-xl text-muted-foreground hover:text-foreground hover:bg-muted/50 transition-colors border border-dashed border-border/50 hover:border-border"
              : "mx-3 mb-3 flex items-center gap-2 px-3 py-2 shrink-0 rounded-xl text-xs font-medium text-muted-foreground hover:text-foreground hover:bg-muted/50 transition-colors border border-dashed border-border/50 hover:border-border"
            }
          >
            <Plus className="w-3.5 h-3.5" />
            {!isCollapsed && "Add Deal"}
          </button>
        </div>
      )}
    </Draggable>
  );
};

// ─── Main Board ──────────────────────────────────────────────────
export const PipelineBoard: React.FC<PipelineBoardProps> = ({
  state,
  onMoveDeal, onAddDeal, onUpdateDeal, onMarkWon, onMarkLost, onReopenDeal, onDeleteDeal,
  onUpdateStageTitle, onUpdateStageBgColor, onDeleteStage, onReorderStages,
}) => {
  const [newDealStageId, setNewDealStageId] = useState<string | null>(null);
  const [selectedDeal, setSelectedDeal] = useState<Deal | null>(null);

  const handleDragEnd = async (result: DropResult) => {
    const { source, destination, type, draggableId } = result;
    if (!destination) return;
    if (source.droppableId === destination.droppableId && source.index === destination.index) return;

    if (type === 'Deal') {
      const DealId = draggableId;
      await onMoveDeal(DealId, destination.droppableId, destination.index);
    } else if (type === 'STAGE') {
      // Extract stage ID from "stage-{id}"
      const stageId = draggableId.replace('stage-', '');
      const newOrder = [...state.stageOrder];
      newOrder.splice(source.index, 1);
      newOrder.splice(destination.index, 0, stageId);
      await onReorderStages(newOrder);
    }
  };

  return (
    <>
      <DragDropContext onDragEnd={handleDragEnd}>
        <Droppable droppableId="pipeline-board" type="STAGE" direction="horizontal">
          {(provided) => (
            <div
              ref={provided.innerRef}
              {...provided.droppableProps}
              className="flex gap-4 h-fit max-h-full pb-6 overflow-x-auto px-1 items-stretch"
              style={{ minHeight: 0 }}
            >
              {state.stageOrder.map((stageId, index) => {
                const stage = state.stageMeta[stageId];
                if (!stage) return null;
                const DealIds = state.stages[stageId] || [];
                const Deals = DealIds.map(id => state.dealsById[id]).filter(Boolean);

                return (
                  <StageColumn
                    key={stageId}
                    stage={stage}
                    Deals={Deals}
                    index={index}
                    onAddDeal={(sid) => setNewDealStageId(sid)}
                    onEditDeal={setSelectedDeal}
                    onUpdateStageTitle={onUpdateStageTitle}
                    onUpdateStageBgColor={onUpdateStageBgColor}
                    onDeleteStage={onDeleteStage}
                  />
                );
              })}
              {provided.placeholder}


            </div>
          )}
        </Droppable>
      </DragDropContext>

      {/* New Deal dialog */}
      <NewDealDialog
        open={!!newDealStageId}
        onOpenChange={(o) => { if (!o) setNewDealStageId(null); }}
        stages={state.stageOrder.map(id => state.stageMeta[id]).filter(Boolean)}
        defaultStageId={newDealStageId || undefined}
        onSubmit={async (data) => {
          await onAddDeal({ ...data, stageId: newDealStageId || data.stageId });
          setNewDealStageId(null);
        }}
      />

      {/* Deal detail dialog */}
      <DealDetailDialog
        deal={selectedDeal}
        stages={state.stageOrder.map(id => state.stageMeta[id]).filter(Boolean)}
        open={!!selectedDeal}
        onOpenChange={(o) => { if (!o) setSelectedDeal(null); }}
        onUpdate={async (id, updates) => {
          const { _localOnly, ...dbUpdates } = updates;
          
          if (!_localOnly && Object.keys(dbUpdates).length > 0) {
            await onUpdateDeal(id, dbUpdates);
          }
          
          // Refresh local selectedDeal
          setSelectedDeal(prev => {
            if (!prev) return prev;
            return {
              ...prev,
              ...updates,
              ...(updates.stageId !== undefined ? { stage_id: updates.stageId } : {})
            };
          });
        }}
        onMarkWon={async (id, reason) => { await onMarkWon(id, reason); setSelectedDeal(null); }}
        onMarkLost={async (id, reason) => { await onMarkLost(id, reason); setSelectedDeal(null); }}
        onReopen={async (id, stageId) => { await onReopenDeal(id, stageId); setSelectedDeal(null); }}
        onDelete={async (id) => { await onDeleteDeal(id); setSelectedDeal(null); }}
        onConvertToClient={async (_) => {
          if (!selectedDeal?.contactId) {
            toast.error('No linked contact found to convert.');
            return;
          }
          try {
            // Need to import leadService
            const { leadService } = await import('@/lib/leadService');
            // Assuming currentWorkspace is not in context here, but wait, PipelineBoard receives deals
            // PipelineBoard doesn't have currentWorkspace. Let's get it from the deals workspace_id
            await leadService.convertLeadToClient(selectedDeal.contactId, selectedDeal.workspace_id);
            toast.success('Lead converted to client successfully');
          } catch (err: any) {
            console.error('Error converting lead:', err);
            toast.error(err.message || 'Failed to convert to client. Note: It may already be a client.');
          }
        }}
      />
    </>
  );
};
